/**
 * ⚠️ DEFERRED — couche SUGGESTION (extraction auto), HORS du chemin actuel
 * (viewer → pont → oplog). Voir `DR/docs/archi-export-plan.md` §17/§23. Conservé
 * car validé (spike S2) et réutilisable au palier suggestion. Les harnais
 * `Facade.run*Spike` ont été retirés.
 * ⚠️ EXCEPTION VIVANTE : `getModelSpaceId` est utilisé par `campaign.ts` /
 * `observations.ts` — ne pas le déplacer/supprimer.
 *
 * Parsing — Découpage en parties via les viewports des présentations.
 *
 * Principe : chaque viewport flottant d'une présentation (layout papier) cadre une
 * fenêtre de l'espace objet = la bbox model-space d'une partie. On calcule ce
 * rectangle depuis `viewCenter`/`viewHeight` + ratio papier, et on cherche le plus
 * gros texte à l'intérieur/au-dessus comme titre de partie.
 *
 * Limites connues :
 *  - suppose twist = 0 et UCS identité ;
 *  - titres : OdDbText seulement ;
 *  - fallback "clustering par vides" non couvert ici (cf. parts.ts).
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import type { DrawingModuleType } from "../DrawingSingleton";
import { openAs } from "../core/oda";
import { deleteObj } from "../core/delete";
import { OpenMode, DictIterType } from "../core/constants";
import { Logger } from "@/logger";

export interface Bbox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  cx: number;
  cy: number;
  w: number;
  h: number;
}

export interface PartViewport {
  layout: string;
  vpNumber: number;
  /** Empreinte du viewport sur le papier. */
  paper: { cx: number; cy: number; w: number; h: number };
  /** Rectangle de l'espace objet cadré par le viewport = bbox de la partie. */
  model: Bbox;
  /** Rotation de la vue (deg). ≠ 0 => calcul de bbox approximatif. */
  twistDeg: number;
  /** Plus gros texte à l'intérieur/au-dessus (candidat titre de partie). */
  title: string | null;
}

export interface ModelText {
  x: number;
  y: number;
  height: number;
  text: string;
}

/** Un texte de l'espace objet. */
function readTextEntity(
  module: DrawingModuleType,
  ent: DrawingModule.OdDbEntity,
): ModelText | null {
  const txt = module.OdDbText.cast(ent);
  if (!txt) return null;
  try {
    const pos = txt.position();
    const s = txt.textString();
    const result: ModelText = {
      x: pos.x,
      y: pos.y,
      height: txt.height(),
      text: s.toString().trim(),
    };
    deleteObj(pos);
    deleteObj(s);
    return result;
  } finally {
    deleteObj(txt);
  }
}

/** Collecte tous les textes de l'espace objet. */
export function collectModelTexts(
  module: DrawingModuleType,
  modelSpaceId: DrawingModule.OdDbObjectId,
): ModelText[] {
  const texts: ModelText[] = [];
  const rec = openAs(
    modelSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForRead,
  );
  const iter = rec.newIterator(true, true, false);
  iter.start(true, true);
  while (!iter.done()) {
    const ent = iter.entity(OpenMode.kForRead, false);
    try {
      const t = readTextEntity(module, ent);
      if (t && t.text.length > 0) texts.push(t);
    } catch {
      // entité illisible — on ignore
    }
    deleteObj(ent);
    iter.step(true, true);
  }
  deleteObj(iter);
  deleteObj(rec);
  return texts;
}

/** Meilleur candidat titre : plus gros texte dans la bbox, ou juste au-dessus. */
export function findTitle(model: Bbox, texts: ModelText[]): string | null {
  const aboveMargin = model.h * 0.6;
  let best: ModelText | null = null;
  for (const t of texts) {
    const inside =
      t.x >= model.minX &&
      t.x <= model.maxX &&
      t.y >= model.minY &&
      t.y <= model.maxY;
    const above =
      t.x >= model.minX &&
      t.x <= model.maxX &&
      t.y >= model.maxY &&
      t.y <= model.maxY + aboveMargin;
    if (inside || above) {
      if (!best || t.height > best.height) best = t;
    }
  }
  return best ? best.text : null;
}

/** bbox model-space cadrée par un viewport. */
function viewportModelBbox(vp: DrawingModule.OdDbViewport): Bbox {
  const vc = vp.viewCenter();
  const vh = vp.viewHeight();
  const pw = vp.width();
  const ph = vp.height();
  const vw = ph !== 0 ? vh * (pw / ph) : vh;
  const cx = vc.x;
  const cy = vc.y;
  deleteObj(vc);
  return {
    minX: cx - vw / 2,
    maxX: cx + vw / 2,
    minY: cy - vh / 2,
    maxY: cy + vh / 2,
    cx,
    cy,
    w: vw,
    h: vh,
  };
}

/** Id du block record de l'espace objet (via le layout "Model"). */
export function getModelSpaceId(
  viewer: DRDrawingViewer,
): DrawingModule.OdDbObjectId | null {
  const module = viewer.module;
  const db = viewer.database;
  if (!db) return null;
  const dict = openAs(
    db.getLayoutDictionaryId(false),
    module.OdDbDictionary,
    OpenMode.kForRead,
  );
  let modelSpaceId: DrawingModule.OdDbObjectId | null = null;
  const it = dict.newIterator(DictIterType.kDictCollated);
  while (!it.done()) {
    if (it.name().toString() === "Model") {
      try {
        const layout = openAs(
          it.objectId(),
          module.OdDbLayout,
          OpenMode.kForRead,
        );
        modelSpaceId = layout.getBlockTableRecordId();
        deleteObj(layout);
      } catch {
        // ignore
      }
    }
    it.next();
  }
  deleteObj(it);
  deleteObj(dict);
  return modelSpaceId;
}

/**
 * Extrait les viewports (parties) de toutes les présentations, avec leur bbox
 * model-space et un candidat titre.
 */
export function extractPartViewports(viewer: DRDrawingViewer): PartViewport[] {
  const module = viewer.module;
  const db = viewer.database;
  if (!db) return [];

  // 1) Énumérer les layouts, capturer le model space, collecter les layouts papier.
  const dictId = db.getLayoutDictionaryId(false);
  const dict = openAs(dictId, module.OdDbDictionary, OpenMode.kForRead);

  let modelSpaceId: DrawingModule.OdDbObjectId | null = null;
  const paperLayouts: Array<{
    name: string;
    blockId: DrawingModule.OdDbObjectId;
  }> = [];

  const it = dict.newIterator(DictIterType.kDictCollated);
  while (!it.done()) {
    const name = it.name().toString();
    const layoutId = it.objectId();
    try {
      const layout = openAs(layoutId, module.OdDbLayout, OpenMode.kForRead);
      const blockId = layout.getBlockTableRecordId();
      if (name === "Model") {
        modelSpaceId = blockId;
      } else {
        paperLayouts.push({ name, blockId });
      }
      deleteObj(layout);
    } catch {
      // layout illisible — on ignore
    }
    it.next();
  }
  deleteObj(it);
  deleteObj(dict);

  const texts = modelSpaceId ? collectModelTexts(module, modelSpaceId) : [];

  // 2) Pour chaque layout papier, itérer ses viewports flottants.
  const result: PartViewport[] = [];
  for (const { name, blockId } of paperLayouts) {
    const rec = openAs(blockId, module.OdDbBlockTableRecord, OpenMode.kForRead);
    const iter = rec.newIterator(true, true, false);
    iter.start(true, true);
    while (!iter.done()) {
      const ent = iter.entity(OpenMode.kForRead, false);
      try {
        const vp = module.OdDbViewport.cast(ent);
        if (vp) {
          const vpNumber = vp.number();
          // number === 1 = viewport "overall" du papier → ignoré.
          const vh = vp.viewHeight();
          if (vpNumber !== 1 && vh > 0) {
            const model = viewportModelBbox(vp);
            const cp = vp.centerPoint();
            const paper = { cx: cp.x, cy: cp.y, w: vp.width(), h: vp.height() };
            deleteObj(cp);
            result.push({
              layout: name,
              vpNumber,
              paper,
              model,
              twistDeg: (vp.twistAngle() * 180) / Math.PI,
              title: findTitle(model, texts),
            });
          }
          deleteObj(vp);
        }
      } catch {
        // entité illisible — on ignore
      }
      deleteObj(ent);
      iter.step(true, true);
    }
    deleteObj(iter);
    deleteObj(rec);
  }

  return result;
}

/** Spike S2 : extrait et logue les parties détectées. */
export async function runViewportSpike(
  viewer: DRDrawingViewer,
): Promise<PartViewport[] | null> {
  if (!viewer.database) {
    Logger.warn("[ViewportSpike] Aucun dessin ouvert.");
    return null;
  }
  const parts = extractPartViewports(viewer);
  Logger.info(
    `[ViewportSpike] ${parts.length} viewport(s) de partie détecté(s).`,
  );
  console.table(
    parts.map((p) => ({
      layout: p.layout,
      vp: p.vpNumber,
      titre: p.title ?? "—",
      "model cx,cy": `${p.model.cx.toFixed(1)}, ${p.model.cy.toFixed(1)}`,
      "model wxh": `${p.model.w.toFixed(1)} x ${p.model.h.toFixed(1)}`,
      twistDeg: p.twistDeg.toFixed(1),
    })),
  );
  return parts;
}
